import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';

const StyledCompassContainer = styled('div')(({ tier, size, variant }) => ({
  position: 'relative',
  width: size,
  height: size,
  borderRadius: '50%',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: tier === 'plus' ? '#f5f5f5' : '#f4d874',
  background: tier === 'plus' 
    ? 'linear-gradient(145deg, #f0f0f0, #d8d8d8, #e5e5e5)'
    : 'linear-gradient(145deg, #f8e49c, #f0d766, #e8c65a)',
  boxShadow: tier === 'plus'
    ? `inset 0 2px 4px rgba(255,255,255,0.7),
       inset 0 -2px 4px rgba(0,0,0,0.2),
       inset 0 1px 2px rgba(255,255,255,0.8),
       0 6px 20px rgba(0,0,0,0.2),
       0 0 0 1px rgba(255,255,255,0.4)`
    : `inset 0 2px 4px rgba(255,248,200,0.7),
       inset 0 -2px 4px rgba(120,90,40,0.3),
       inset 0 1px 2px rgba(255,255,255,0.6),
       0 6px 20px rgba(200,150,80,0.3),
       0 0 0 1px rgba(240,215,102,0.5)`,

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: tier === 'plus'
      ? `inset 0 2px 4px rgba(255,255,255,0.8),
         inset 0 -2px 4px rgba(0,0,0,0.25),
         inset 0 1px 2px rgba(255,255,255,0.85),
         0 8px 25px rgba(0,0,0,0.25),
         0 0 0 1px rgba(255,255,255,0.5)`
      : `inset 0 2px 4px rgba(255,248,200,0.8),
         inset 0 -2px 4px rgba(120,90,40,0.4),
         inset 0 1px 2px rgba(255,255,255,0.7),
         0 8px 25px rgba(200,150,80,0.4),
         0 0 0 1px rgba(240,215,102,0.6)`,
    
    ...(variant === 'hover' && {
      '& .compass-inner': {
        transform: 'rotate(360deg)',
      },
    }),
  },

  '&:active': {
    transform: 'translateY(-2px)',
  },

  // Enhanced glow effect for ambassador tier
  ...(tier === 'ambassador' && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -3,
      left: -3,
      right: -3,
      bottom: -3,
      background: 'linear-gradient(45deg, #fff2a6, #f4d874, #e6c547, #dda15e, #f4d874, #fff2a6)',
      borderRadius: '50%',
      zIndex: -1,
      opacity: 0,
      transition: 'opacity 0.3s ease',
      filter: 'blur(1px)',
    },
    '&:hover::before': {
      opacity: 0.8,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -1,
      right: -1,
      bottom: -1,
      background: 'linear-gradient(45deg, #f9e79f, #f4d874)',
      borderRadius: '50%',
      zIndex: -1,
      opacity: 0.6,
    },
  }),
}));

const CompassInner = styled('div')(({ size, animate, variant }) => ({
  width: size * 0.9,
  height: size * 0.9,
  background: 'transparent',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  transition: variant === 'static' ? 'none' : 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: animate ? 'rotate(360deg)' : 'rotate(0deg)',
}));

const SimplifiedCompass = ({ size }) => (
  <svg width={size * 0.85} height={size * 0.85} viewBox="0 0 100 100">
    {/* Outer compass ring - very thick for visibility */}
    <circle cx="50" cy="50" r="45" fill="none" stroke="white" strokeWidth="10" opacity="0.95"/>
    <circle cx="50" cy="50" r="35" fill="none" stroke="white" strokeWidth="3" opacity="0.7"/>
    
    {/* Main compass needle - much thicker */}
    <path d="M50 12 L58 48 L50 44 L42 48 Z" fill="white" stroke="white" strokeWidth="1"/>
    <path d="M50 88 L42 52 L50 56 L58 52 Z" fill="white" opacity="0.8" stroke="white" strokeWidth="1"/>

    {/* Large green center circle */}
    <circle cx="50" cy="50" r="8" fill="#668355"/>
    <circle cx="50" cy="50" r="4" fill="white" opacity="0.9"/>
  </svg>
);

const DetailedCompass = ({ size }) => (
  <svg width={size * 0.85} height={size * 0.85} viewBox="0 0 100 100">
    {/* Outer compass ring - very thick for visibility */}
    <circle cx="50" cy="50" r="45" fill="none" stroke="white" strokeWidth="10" opacity="0.95"/>
    <circle cx="50" cy="50" r="36" fill="none" stroke="white" strokeWidth="2" opacity="0.7"/>
    
    {/* Main compass points - much thicker */}
    <path d="M50 8 L56 46 L50 42 L44 46 Z" fill="white" stroke="white" strokeWidth="1"/>
    <path d="M92 50 L54 56 L58 50 L54 44 Z" fill="white" opacity="0.85" stroke="white" strokeWidth="1"/>
    <path d="M50 92 L44 54 L50 58 L56 54 Z" fill="white" opacity="0.85" stroke="white" strokeWidth="1"/>
    <path d="M8 50 L46 44 L42 50 L46 56 Z" fill="white" opacity="0.85" stroke="white" strokeWidth="1"/>
    
    {/* Large green center circle */}
    <circle cx="50" cy="50" r="8" fill="#668355"/>
    <circle cx="50" cy="50" r="4" fill="white" opacity="0.9"/>
    
    {/* Thicker compass markings */}
    <g stroke="white" strokeWidth="3" opacity="0.8">
      <line x1="50" y1="13" x2="50" y2="20"/>
      <line x1="50" y1="80" x2="50" y2="87"/>
      <line x1="87" y1="50" x2="80" y2="50"/>
      <line x1="20" y1="50" x2="13" y2="50"/>
    </g>
  </svg>
);

export const CompassIcon = ({ 
  tier, 
  size = 30, 
  onClick,
  variant = 'hover' //'static' | 'hover' | 'auto-animated'
}) => {
  const [animate, setAnimate] = useState(false);

  // Auto-animation for animated variant
  useEffect(() => {
    if (variant !== "auto-animated") return;

    // Initial animation on mount
    const initialTimeout = setTimeout(() => setAnimate(true), 300);
    
    // Recurring animation every ~X seconds
    const interval = setInterval(() => {
      setAnimate(true);
    }, 10000 + Math.random() * 2000); 

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [variant]);

  // Reset animation state
  useEffect(() => {
    if (animate) {
      const timeout = setTimeout(() => setAnimate(false), 1400);
      return () => clearTimeout(timeout);
    }
  }, [animate]);

  return (
    <StyledCompassContainer 
      tier={tier} 
      size={size} 
      variant={variant}
      onClick={onClick}
    >
      <CompassInner 
        className="compass-inner" 
        size={size}
        animate={animate}
        variant={variant}
      >
        {tier === 'plus' ? (
          <SimplifiedCompass size={size} />
        ) : (
          <DetailedCompass size={size} />
        )}
      </CompassInner>
    </StyledCompassContainer>
  );
};

export default CompassIcon;